import * as React from 'react'

import { Box, Button, useClassNames } from '@factorialco/deprecated-design-system'
import { PeopleChartGroupFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import TeamCard from 'modules/people_chart/components/TeamCard'
import { Dimensions } from 'modules/people_chart/lib/Dimensions'
import { HierarchyLink } from 'modules/people_chart/lib/Hierarchy'
import type { PointNode } from 'modules/people_chart/lib/PointNode'

import './index.styles.css'

const stylesRefs = {
  addNodeRight: 'Group_addNodeRight',
  addNodeBottom: 'Group_addNodeBottom',
  hidden: 'Group_hidden',
}

type Relationship = 'sibling' | 'child'

type Props = {
  node: PointNode<HierarchyLink>
  size: Dimensions
  group: PeopleChartGroupFragment
  editing: boolean
  onGroupClick: (id: number) => void
  onNodeAdded: (id: string) => void
  setOpenSelector: React.Dispatch<React.SetStateAction<boolean>>
  setModalPosition: React.Dispatch<React.SetStateAction<{ top: string; left: string }>>
  active: boolean
}

const Group: React.FC<Props> = ({
  node,
  size,
  group,
  editing,
  onNodeAdded,
  onGroupClick,
  setOpenSelector,
  setModalPosition,
  active,
}) => {
  const [width, height] = size
  const siblingButtonRef = React.useRef<HTMLDivElement>(null)
  const childButtonRef = React.useRef<HTMLDivElement>(null)
  const cx = useClassNames(stylesRefs)
  const teamSelectorModalWidth = 400
  const handleButtonClick = (
    relationship: Relationship,
    buttonRef: React.RefObject<HTMLDivElement>
  ) => {
    const buttonRect = buttonRef.current?.getBoundingClientRect()
    if (!buttonRect) return

    const modalTop = buttonRect.bottom + 8
    const modalLeft = buttonRect.left + buttonRect.width / 2 - teamSelectorModalWidth / 2
    const parentId = relationship === 'sibling' ? node.parent?.id : node.id
    setModalPosition({ top: String(modalTop), left: String(modalLeft) })
    onNodeAdded(parentId || '0')
    setOpenSelector(true)
  }

  return (
    <foreignObject
      style={{ overflow: 'visible' }}
      width={width}
      height={height}
      x={node.x}
      y={node.y}
    >
      <Box position="relative">
        <Box className={cx('addNodeRight', { hidden: !editing })} ref={siblingButtonRef}>
          <Button
            aria-label={i18n.t('people_chart.add_sibling')}
            type="secondary"
            iconName="add"
            onClick={() => handleButtonClick('sibling', siblingButtonRef)}
          />
        </Box>
        <Box className={cx('addNodeBottom', { hidden: !editing })} ref={childButtonRef}>
          <Button
            aria-label={i18n.t('people_chart.add_child')}
            type="secondary"
            iconName="add"
            onClick={() => handleButtonClick('child', childButtonRef)}
          />
        </Box>
        <TeamCard active={active} team={group} onClick={onGroupClick} />
      </Box>
    </foreignObject>
  )
}

export default Group
